import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useProjects } from '../hooks/useProjects';
import { useAuth } from '../context/AuthContext';
import { OPENAI_CHAT_MODEL_OPTIONS } from '../constants/openaiModels';

const Projects = () => {
  const { currentUser } = useAuth();
  
  // Debug: Check what's in currentUser
  React.useEffect(() => {
    console.log('Projects - currentUser:', currentUser);
    console.log('Projects - localStorage user:', JSON.parse(localStorage.getItem('user') || 'null'));
  }, [currentUser]);
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { useAllProjects, useCreateProject, useDuplicateProject, useDeleteProject } = useProjects();
  const { data: projectsData, isLoading, error, refetch } = useAllProjects();
  const createProjectMutation = useCreateProject();
  const duplicateProjectMutation = useDuplicateProject();
  const deleteProjectMutation = useDeleteProject();
  
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    clientName: '',
    model: 'gpt-4o',
    secondaryModel: 'gpt-4o-mini'
  });

  const [duplicateProject, setDuplicateProject] = useState({
    name: '',
    description: '',
    clientName: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [duplicateFormErrors, setDuplicateFormErrors] = useState({});
  
  const projects = projectsData || [];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDuplicateInputChange = (e) => {
    const { name, value } = e.target;
    setDuplicateProject(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (duplicateFormErrors[name]) {
      setDuplicateFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDuplicateClick = (project) => {
    setProjectToDuplicate(project);
    setDuplicateProject({
      name: `${project.name} (Copy)`,
      description: project.description || '',
      clientName: project.clientName || ''
    });
    setShowDuplicateForm(true);
    setDuplicateFormErrors({});
  };
  
  const validateForm = () => {
    const errors = {};
    if (!newProject.name.trim()) {
      errors.name = 'Project name is required';
    }
    if (!newProject.clientName.trim()) {
      errors.clientName = 'Client name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDuplicateForm = () => {
    const errors = {};
    if (!duplicateProject.name.trim()) {
      errors.name = 'Project name is required';
    }
    if (!duplicateProject.clientName.trim()) {
      errors.clientName = 'Client name is required';
    }
    
    setDuplicateFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await createProjectMutation.mutateAsync(newProject);
      setNewProject({
        name: '',
        description: '',
        clientName: '',
        model: 'gpt-4o',
        secondaryModel: 'gpt-4o-mini'
      });
      setShowCreateForm(false);
      refetch(); // Refresh the projects list
    } catch (error) {
      console.error('Error creating project:', error);
      setFormErrors({
        submit: 'Failed to create project. Please try again.'
      });
    }
  };

  const handleDuplicateProject = async (e) => {
    e.preventDefault();
    
    if (!validateDuplicateForm()) {
      return;
    }
    
    try {
      const result = await duplicateProjectMutation.mutateAsync({
        projectId: projectToDuplicate._id,
        data: duplicateProject
      });
      
      // Show success message with duplication counts
      const counts = result.duplicatedCounts || {};
      const successMessage = `Project duplicated successfully! Copied: ${counts.apiEndpoints || 0} API endpoints, ${counts.locations || 0} locations, ${counts.knowledgeDocuments || 0} knowledge documents, and ${counts.humanAgents || 0} human agents.`;
      
      // You can replace this alert with a proper toast notification
      alert(successMessage);
      
      setDuplicateProject({
        name: '',
        description: '',
        clientName: ''
      });
      setShowDuplicateForm(false);
      setProjectToDuplicate(null);
      refetch(); // Refresh the projects list
    } catch (error) {
      console.error('Error duplicating project:', error);
      setDuplicateFormErrors({
        submit: 'Failed to duplicate project. Please try again.'
      });
    }
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    
    try {
      const result = await deleteProjectMutation.mutateAsync(projectToDelete._id);
      
      // Show success message with deletion counts
      const counts = result.deletedCounts || {};
      const successMessage = `Project "${projectToDelete.name}" deleted successfully! Removed: ${counts.apiEndpoints || 0} API endpoints, ${counts.locations || 0} locations, ${counts.knowledgeDocuments || 0} knowledge documents, ${counts.humanAgents || 0} human agents, ${counts.chatLogs || 0} chat logs, and ${counts.costs || 0} cost records.`;
      
      alert(successMessage);
      
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
      refetch(); // Refresh the projects list
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setProjectToDelete(null);
  };

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Projects</h1>
          {!currentUser?.isSubUser && (
          <button 
            className="btn btn-primary text-smd"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : 'Create New Project'}
          </button>
          )}
        </div>

        {showCreateForm && (
          <div className="card p-6 mt-4">
            <h2 className="text-xl font-medium mb-4">Create New Project</h2>
            <form onSubmit={handleCreateProject}>
              <div className="form-group mb-4">
                <label htmlFor="name" className="form-label">
                  Project Name*
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                  value={newProject.name}
                  onChange={handleInputChange}
                  placeholder="Enter project name"
                />
                {formErrors.name && (
                  <p className="form-error">{formErrors.name}</p>
                )}
              </div>

              <div className="form-group mb-4">
                <label htmlFor="clientName" className="form-label">
                  Client Name*
                </label>
                <input
                  type="text"
                  id="clientName"
                  name="clientName"
                  className={`form-input ${formErrors.clientName ? 'border-red-500' : ''}`}
                  value={newProject.clientName}
                  onChange={handleInputChange}
                  placeholder="Enter client name"
                />
                {formErrors.clientName && (
                  <p className="form-error">{formErrors.clientName}</p>
                )}
              </div>

              <div className="form-group mb-4">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  className="form-input"
                  value={newProject.description}
                  onChange={handleInputChange}
                  placeholder="Enter project description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label htmlFor="model" className="form-label">
                    Primary LLM Model
                  </label>
                  <select
                    id="model"
                    name="model"
                    className="form-input"
                    value={newProject.model}
                    onChange={handleInputChange}
                  >
                    {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="secondaryModel" className="form-label">
                    Secondary LLM Model
                  </label>
                  <select
                    id="secondaryModel"
                    name="secondaryModel"
                    className="form-input"
                    value={newProject.secondaryModel}
                    onChange={handleInputChange}
                  >
                    {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                      <option key={model.value} value={model.value}>{model.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formErrors.submit && (
                <p className="form-error mb-4">{formErrors.submit}</p>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createProjectMutation.isLoading}
                >
                  {createProjectMutation.isLoading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        )}

        {showDuplicateForm && projectToDuplicate && (
          <div className="card p-6 mt-4">
            <h2 className="text-xl font-medium mb-4">Duplicate Project: {projectToDuplicate.name}</h2>
            <form onSubmit={handleDuplicateProject}>
              <div className="form-group mb-4">
                <label htmlFor="duplicate-name" className="form-label">
                  Project Name*
                </label>
                <input
                  type="text"
                  id="duplicate-name"
                  name="name"
                  className={`form-input ${duplicateFormErrors.name ? 'border-red-500' : ''}`}
                  value={duplicateProject.name}
                  onChange={handleDuplicateInputChange}
                  placeholder="Enter project name"
                />
                {duplicateFormErrors.name && (
                  <p className="form-error">{duplicateFormErrors.name}</p>
                )}
              </div>

              <div className="form-group mb-4">
                <label htmlFor="duplicate-clientName" className="form-label">
                  Client Name*
                </label>
                <input
                  type="text"
                  id="duplicate-clientName"
                  name="clientName"
                  className={`form-input ${duplicateFormErrors.clientName ? 'border-red-500' : ''}`}
                  value={duplicateProject.clientName}
                  onChange={handleDuplicateInputChange}
                  placeholder="Enter client name"
                />
                {duplicateFormErrors.clientName && (
                  <p className="form-error">{duplicateFormErrors.clientName}</p>
                )}
              </div>

              <div className="form-group mb-4">
                <label htmlFor="duplicate-description" className="form-label">
                  Description
                </label>
                <textarea
                  id="duplicate-description"
                  name="description"
                  rows="3"
                  className="form-input"
                  value={duplicateProject.description}
                  onChange={handleDuplicateInputChange}
                  placeholder="Enter project description"
                />
              </div>

              {duplicateFormErrors.submit && (
                <p className="form-error mb-4">{duplicateFormErrors.submit}</p>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-secondary mr-2 text-smd"
                  onClick={() => {
                    setShowDuplicateForm(false);
                    setProjectToDuplicate(null);
                    setDuplicateProject({ name: '', description: '', clientName: '' });
                    setDuplicateFormErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary text-smd"
                  disabled={duplicateProjectMutation.isLoading}
                >
                  {duplicateProjectMutation.isLoading ? 'Duplicating...' : 'Duplicate Project'}
                </button>
              </div>
            </form>
          </div>
        )}

        {showDeleteConfirm && projectToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-medium mb-4 text-red-600">Delete Project</h2>
              <p className="mb-4">
                Are you sure you want to delete the project <strong>"{projectToDelete.name}"</strong>?
              </p>
              <p className="mb-6 text-sm text-gray-600">
                This action will permanently delete the project and all associated data including:
                API endpoints, locations, knowledge base documents, human agents, chat logs, and cost records.
                <strong className="text-red-600"> This action cannot be undone.</strong>
              </p>
              
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary text-smd"
                  onClick={handleDeleteCancel}
                  disabled={deleteProjectMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger text-smd ml-2"
                  onClick={handleDeleteConfirm}
                  disabled={deleteProjectMutation.isLoading}
                >
                  {deleteProjectMutation.isLoading ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="card p-4 text-center mt-4">
            <p>Loading projects...</p>
          </div>
        ) : error ? (
          <div className="card p-4 text-center mt-4">
            <p>Error loading projects. Please try again.</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="card p-6 mt-4">
            <h3 className="text-lg font-medium mb-2">
              {currentUser?.isSubUser ? 'No accessible projects' : 'No projects yet'}
            </h3>
            <p className="mb-4">
              {currentUser?.isSubUser 
                ? 'You don\'t have access to any projects yet. Contact your administrator to grant you access to projects.'
                : 'Create your first chatbot project to get started.'
              }
            </p>
            {!currentUser?.isSubUser && (
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              Create Your First Project
            </button>
            )}
          </div>
        ) : (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => (
                <div key={project._id} className="card p-4">
                  <h3 className="text-lg font-medium mb-2">{project.name}</h3>
                  <p className="mb-4 text-sm">{project.description || 'No description provided'}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {project.primaryLLM?.model || project.model || 'GPT-4o'}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {project.stats?.totalSessions || project.sessions || 0} Sessions
                    </span>
                    {project.clientName && (
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        Client: {project.clientName}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between gap-2">
                      <Link to={`/projects/${project._id}`} className="btn btn-secondary text-smd flex-1">
                        View Details
                      </Link>
                      <Link to={`/projects/${project._id}/simulator`} className="btn btn-primary text-smd flex-1">
                        Test Chatbot
                      </Link>
                    </div>
                    {!currentUser?.isSubUser && (
                    <div className="flex justify-between gap-2 mt-4">
                      <button
                        onClick={() => handleDuplicateClick(project)}
                        className="btn btn-secondary text-smd flex-1"
                        disabled={duplicateProjectMutation.isLoading}
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={() => handleDeleteClick(project)}
                        className="btn btn-danger text-smd"
                        disabled={deleteProjectMutation.isLoading}
                      >
                        Delete
                      </button>
                      </div>
                    )}
                    {/* Temporary Debug Info */}
                    <div className="mt-2 p-2 bg-yellow-100 text-xs">
                      <div>Debug: isSubUser = {currentUser?.isSubUser ? 'true' : 'false'}</div>
                      <div>shouldShowButtons = {!currentUser?.isSubUser ? 'true' : 'false'}</div>
                      <div>accessibleProjects = {currentUser?.accessibleProjects?.length || 0}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Projects; 